<template>
  <div
    class="select-trigger"
    :class="[`select-trigger--${theme}`]"
  >
    <div
      v-if="!!label"
      class="select-trigger__label select-trigger__label--input"
      :class="{
        'select-trigger__label--active': isOpened || hasSelected,
        'select-trigger__label--error': error
      }"
    >
      {{ label }}
    </div>

    <div
      class="select-trigger__input"
      :class="[
        `select-trigger__input--${theme}`,
        {
          'select-trigger__input--error': error,
          'select-trigger__input--opened': isOpened,
          'select-trigger__input--has-selected': hasSelected
        }
      ]"
    >
      <span
        class="select-trigger__input-placeholder"
        :class="{
          'select-trigger__input-placeholder--has-selected': isOpened || hasSelected || !label
        }"
      >
        <template v-if="slots['base-trigger-content']">
          <slot
            name="base-trigger-content"
          />
        </template>
        <template v-else>
          {{ placeholder }}
        </template>
      </span>
      <span
        class="select-trigger__icon"
        :class="[
          `select-trigger__icon--${theme}`,
          `icon-caret-${isOpened ? 'up' : 'down'}`
        ]"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
type Props = {
  placeholder: string
  theme?: 'default' | 'simple'
  label?: string
  hasSelected?: boolean
  isOpened?: boolean
  error?: boolean
}

withDefaults(defineProps<Props>(), {
  theme: 'default',
  label: '',
  error: false
})

const slots = useSlots()

</script>

<style lang="scss" scoped>
.select-trigger {
  $root: &;
  cursor: pointer;
  position: relative;

  &__input {
    &--default {
      font-size: 1.4rem;
      height: 4.2rem;
      padding: 0 1.2rem;
      white-space: nowrap;
      display: flex;
      align-items: center;
      justify-content: space-between;
      color: $font-color;
      border: solid 1px #e7e7e7;
      border-radius: 4px;
      transition: border-color $transition-time;

      &#{$root}__input--has-selected {
        color: $font-color;
      }

      &#{$root}__input--opened {
        color: $font-color;
        border-color: rgba(#161616, 0.5);
      }

      &#{$root}__input--error {
        border-color: $error-color;

        &:hover {
          border-color: $error-color;
        }
      }

      &:hover {
        color: $font-color;
        border-color: rgba(#161616, 0.5);
      }
    }

    &--simple {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
  }

  &__input-placeholder {
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    color: transparent;
    &--has-selected {
      color: $font-color;
    }
  }

  &__icon {
    color: rgba(22, 22, 22, 0.7);

    &--simple {
      margin-left: 2rem;
      position: relative;
      top: -0.1rem;
    }
  }

  &__label {
    color: $gray-color;
    margin-bottom: 0.5rem;

    &--error {
      color: $error-color !important;
    }
  }

  &__label--input {
    font-size: 1.4rem;
    margin-bottom: 0.5rem;
    position: absolute;
    color: #8a8a8a;
    padding: 0 0.5rem;
    display: inline-block;
    pointer-events: none;
    left: 0.7rem;
    top: 2.1rem;
    transform: translateY(-50%);
    transition: 0.2s ease all;
    background-color: #fff;

    &#{$root}__label--active {
      font-size: 1.2rem;
      top: 0;
    }
  }
}
</style>
